import { IconRefresh, IconSparkles } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { __ } from "@common/helpers/i18n";
import { Button } from "@app/elements/form";
import { Modal } from "@app/elements/modals";

const snoozeDuration = 30 * 60 * 1000;

type PwaUpdateModalProps = {
  isUpdating: boolean;
  onUpdate: () => void;
  show: boolean;
};

export default function PwaUpdateModal({
  isUpdating,
  onUpdate,
  show,
}: PwaUpdateModalProps) {
  const [dismissedUntil, setDismissedUntil] = useState(0);
  const isSnoozed = dismissedUntil > Date.now();

  useEffect(() => {
    if (!show || !isSnoozed) return undefined;

    const timeout = window.setTimeout(
      () => setDismissedUntil(0),
      Math.max(0, dismissedUntil - Date.now()),
    );

    return () => window.clearTimeout(timeout);
  }, [dismissedUntil, isSnoozed, show]);

  if (!show || isSnoozed) return null;

  return (
    <Modal
      isOpen
      onClose={() => setDismissedUntil(Date.now() + snoozeDuration)}
      showCloseIcon
      size="small"
      padding="none"
      panelClassName="max-w-lg"
      viewportClassName="items-center py-4"
    >
      <Modal.Header className="pe-14">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <IconSparkles size={20} stroke={2} />
          </span>
          <div className="min-w-0">
            <Modal.Title className="text-lg font-extrabold leading-7">
              {__("A new version is ready")}
            </Modal.Title>
            <Modal.Description className="mt-1 text-sm font-medium leading-6 text-muted-foreground">
              {__(
                "Activate the latest version for recent fixes and a smoother experience.",
              )}
            </Modal.Description>
          </div>
        </div>
      </Modal.Header>

      <Modal.Footer>
        <Button
          type="button"
          variant="primary"
          className="h-10 justify-center px-4"
          icon={<IconRefresh size={18} />}
          loading={isUpdating}
          onClick={onUpdate}
        >
          {__("Update now")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
